import React, { useEffect, useRef, useState } from "react";

// --- Configuration ---
const WIDTH = 400;
const HEIGHT = 64;

// --- Cadence Mapping Engine ---
interface AnimationEvent {
  type: "pulse" | "pause";
  duration: number;
  amplitude?: number;
}

const generateCadenceMap = (message: string): AnimationEvent[] => {
  if (!message) return [];

  const words = message.split(/[\s,.]+/).filter(Boolean);
  const cadence: AnimationEvent[] = [];

  words.forEach((word, index) => {
    const duration = 150 + Math.min(word.length * 50, 600);
    const amplitude = 0.75 + Math.min(word.length / 10, 0.25);
    cadence.push({ type: "pulse", duration, amplitude });

    if (index < words.length - 1) {
      const pauseDuration = 100 + Math.random() * 150;
      cadence.push({ type: "pause", duration: pauseDuration });
    }
  });

  return cadence;
};

// --- Path Generation Logic ---
const generateWaveY = (
  x: number,
  time: number,
  excitement: number,
  baseAmplitude: number,
  maxDetailAmplitude: number
) => {
  const baseFrequency = 2.5;
  const baseSpeed = 0.002;

  const detailFrequency = 6;
  const detailSpeed = 0.008;

  const pulse1 = Math.sin(time * 0.0015);
  const pulse2 = Math.sin(time * 0.0022);
  const rawPulse = Math.abs((pulse1 + pulse2) / 2);

  const minPulse = 0.1;
  const excitementPulse = minPulse + (1 - minPulse) * rawPulse;
  const currentDetailAmplitude =
    maxDetailAmplitude * excitementPulse * excitement;

  const amplitudeEnvelope = Math.pow(Math.sin((Math.PI * x) / WIDTH), 2);
  const baseWave =
    Math.sin((x * baseFrequency) / WIDTH + time * baseSpeed) * baseAmplitude;
  const detailWave =
    Math.sin((x * detailFrequency) / WIDTH + time * detailSpeed) *
    currentDetailAmplitude;

  return HEIGHT / 2 + (baseWave + detailWave) * amplitudeEnvelope;
};

const createShapePath = (points: number[]) => {
  if (points.length === 0) return "";
  const midY = HEIGHT / 2;
  let path = `M 0 ${midY}`;
  points.forEach((y, x) => {
    path += ` L ${x} ${y}`;
  });
  path += ` L ${WIDTH} ${midY} Z`;
  return path;
};

const generatePulseY = (
  time: number,
  amplitude: number,
  pulseWidth: number,
  pulseOffset: number,
  pulseAmplitudeMultiplier: number
) => {
  const points: number[] = [];
  const startX = WIDTH * pulseOffset;
  const endX = startX + WIDTH * pulseWidth;
  const waveWidth = endX - startX;

  for (let x = 0; x <= WIDTH; x++) {
    let envelope = 0;
    if (x >= startX && x <= endX) {
      const normalizedX = (x - startX) / waveWidth;
      envelope = Math.pow(Math.sin(normalizedX * Math.PI), 2);
    }
    const wave = Math.sin(time * 0.005);
    const y =
      HEIGHT / 2 + wave * amplitude * envelope * pulseAmplitudeMultiplier;
    points.push(y);
  }
  return points;
};

interface AniWaveformProps {
  isActive?: boolean;
  message?: string;
  baseAmplitude?: number;
  maxDetailAmplitude?: number;
  pulseAmplitudeMultiplier?: number;
  thinkingExcitement?: number;
  speakingExcitement?: number;
  backgroundWaveOpacity?: number;
  repeat?: boolean;
}

export const AniWaveform = ({
  isActive = false,
  message = "",
  baseAmplitude = 3,
  maxDetailAmplitude = 18,
  pulseAmplitudeMultiplier = 18,
  thinkingExcitement = 0.2,
  speakingExcitement = 1.0,
  backgroundWaveOpacity = 0.9,
  repeat = false,
}: AniWaveformProps) => {
  const [renderState, setRenderState] = useState({
    blue: "",
    pink: "",
    teal: "",
    opacity: 0,
  });
  const animationFrameRef = useRef<number | null>(null);

  const stateRef = useRef({
    cadenceMap: [] as AnimationEvent[],
    eventIndex: 0,
    lastProcessedEventIndex: -1,
    eventStartTime: 0,
    excitement: 0,
    pulseAmplitude: 0,
    pulseWidth: 0.5,
    pulseOffset: 0.25,
  });

  const isActiveRef = useRef(isActive);
  const animationStoppedRef = useRef(false); // Track if animation has stopped due to fade-out
  
  useEffect(() => {
    isActiveRef.current = isActive;
    // When isActive changes, reset the stopped flag so animation can restart
    // (The main effect will re-run since isActive is in its dependencies)
    if (isActive) {
      animationStoppedRef.current = false;
    }
  }, [isActive]);

  const messageRef = useRef(message);
  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  useEffect(() => {
    const messageForCadence =
      message || "I am thinking about what to say next.";
    stateRef.current.cadenceMap = generateCadenceMap(messageForCadence);
    stateRef.current.eventIndex = 0;
    stateRef.current.lastProcessedEventIndex = -1;
    stateRef.current.eventStartTime = 0;
  }, [message]);

  useEffect(() => {
    let startTime = 0;
    let isCleanedUp = false;

    const animate = (timestamp: number) => {
      if (isCleanedUp) return; // Prevent animation if component is unmounted

      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;
      const state = stateRef.current;
      const isCurrentlyActive = isActiveRef.current;
      const hasRealMessage = !!messageRef.current;

      if (isCurrentlyActive) {
        const targetExcitement = hasRealMessage
          ? speakingExcitement
          : thinkingExcitement;
        state.excitement += (targetExcitement - state.excitement) * 0.1;

        if (state.eventStartTime === 0) state.eventStartTime = elapsedTime;
        const currentEvent = state.cadenceMap[state.eventIndex];

        if (currentEvent) {
          if (state.eventIndex > state.lastProcessedEventIndex) {
            if (currentEvent.type === "pulse") {
              const minWidth = 0.2;
              const maxWidth = 0.5;
              state.pulseWidth =
                minWidth + Math.random() * (maxWidth - minWidth);
              const centralZoneStart = 0.2;
              const centralZoneEnd = 0.8;
              const minOffset = centralZoneStart;
              const maxOffset = centralZoneEnd - state.pulseWidth;
              state.pulseOffset =
                minOffset + Math.random() * (maxOffset - minOffset);
            }
            state.lastProcessedEventIndex = state.eventIndex;
          }

          const eventElapsedTime = elapsedTime - state.eventStartTime;
          if (currentEvent.type === "pulse") {
            const progress = Math.min(
              eventElapsedTime / currentEvent.duration,
              1
            );
            const pulseValue =
              Math.sin(progress * Math.PI) * (currentEvent.amplitude || 0);
            state.pulseAmplitude = pulseValue;
          } else {
            state.pulseAmplitude = 0;
          }

          if (eventElapsedTime >= currentEvent.duration) {
            state.eventIndex++;
            state.eventStartTime = elapsedTime;
          }
        } else {
          // End of cadence map reached
          if (repeat && state.cadenceMap.length > 0) {
            // Restart the cadence cycle
            state.eventIndex = 0;
            state.eventStartTime = elapsedTime;
            state.lastProcessedEventIndex = -1;
          } else {
            // Fade out when not repeating
            state.pulseAmplitude *= 0.9;
          }
        }
      } else {
        state.excitement *= 0.9;
        state.pulseAmplitude *= 0.9;

        if (state.excitement < 0.01) {
          state.eventIndex = 0;
          state.lastProcessedEventIndex = -1;
          state.eventStartTime = 0;
        }
      }

      // --- Path & Opacity Generation ---
      const energy = state.excitement + state.pulseAmplitude;
      const opacity = Math.min(energy * 3, 1); // Multiply to fade in faster

      const topWavePoints: number[] = [];
      for (let x = 0; x <= WIDTH; x++) {
        topWavePoints.push(
          generateWaveY(
            x,
            elapsedTime,
            state.excitement,
            baseAmplitude,
            maxDetailAmplitude
          )
        );
      }
      const bottomWavePoints = topWavePoints.map((y) => HEIGHT - y);
      const pulsePoints = generatePulseY(
        elapsedTime,
        state.pulseAmplitude,
        state.pulseWidth,
        state.pulseOffset,
        pulseAmplitudeMultiplier
      );
      const pulseSide = stateRef.current.eventIndex % 2 === 0 ? 1 : -1;
      const pulsePointsTransformed = pulsePoints.map(
        (p) => HEIGHT / 2 + (p - HEIGHT / 2) * pulseSide
      );

      if (!isCleanedUp) {
        // Stop animation loop when inactive AND fully faded out to prevent unnecessary renders
        const isFullyFaded = !isCurrentlyActive && state.excitement < 0.001 && state.pulseAmplitude < 0.001;
        
        if (isFullyFaded) {
          // Only update state if it changed (avoid unnecessary re-renders)
          setRenderState((prev) => {
            if (prev.opacity !== 0 || prev.blue !== "" || prev.pink !== "" || prev.teal !== "") {
              return { blue: "", pink: "", teal: "", opacity: 0 };
            }
            return prev; // Return same reference to avoid re-render
          });
          // Don't schedule another frame - animation is complete
          animationStoppedRef.current = true;
          animationFrameRef.current = null;
          return;
        }

        setRenderState({
          blue: createShapePath(topWavePoints),
          pink: createShapePath(bottomWavePoints),
          teal: createShapePath(pulsePointsTransformed),
          opacity: opacity,
        });
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      isCleanedUp = true;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [
    isActive, // Added: restart animation when isActive changes
    baseAmplitude,
    maxDetailAmplitude,
    pulseAmplitudeMultiplier,
    speakingExcitement,
    thinkingExcitement,
    repeat,
  ]);

  return (
    <svg
      width="100%"
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      style={{
        display: "block",
        pointerEvents: "none",
        opacity: renderState.opacity,
      }}
    >
      <g style={{ mixBlendMode: "lighten" }}>
        <path
          d={renderState.blue}
          fill={`rgba(167, 191, 250, ${backgroundWaveOpacity})`}
        />
        <path
          d={renderState.pink}
          fill={`rgba(192, 132, 252, ${backgroundWaveOpacity})`}
        />
        <path d={renderState.teal} fill="rgba(52, 255, 228, 0.9)" />
      </g>
    </svg>
  );
};
